import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import {
  BriefcaseBusiness,
  Clock3,
  Pencil,
  Plus,
  Receipt,
  Search,
  TimerReset,
  Trash2,
  Users2,
} from "lucide-react";
import { db } from "../firebase";
import { usePanelTheme } from "../context/themeContext";
import PageShell from "../components/PageShell";
import PanelCard from "../components/PanelCard";
import SectionHeading from "../components/SectionHeading";
import UIButton from "../components/UIButton";
import UIInput from "../components/UIInput";
import UISelect from "../components/UISelect";
import UILabel from "../components/UILabel";
import { notify } from "../utils/notify";

const initialForm = {
  date: "",
  workerId: "",
  workerName: "",
  projectId: "",
  projectName: "",
  startTime: "08:00",
  endTime: "16:00",
  breakMinutes: "30",
  hourlyRate: "",
  status: "open",
  notes: "",
};

const ui = {
  sq: {
    title: "Timesheets / Orët e punës",
    subtitle: "Regjistro orët e punës për projekte, punëtorë dhe llogarit koston e punës.",
    newEntry: "Orar i ri",
    editEntry: "Ndrysho orarin",
    totalHours: "Orë totale",
    thisWeek: "Kjo javë",
    activeWorkers: "Punëtorë aktivë",
    laborCost: "Kosto pune",
    searchPlaceholder: "Kërko sipas punëtorit, projektit ose shënimit",
    allStatuses: "Të gjitha statuset",
    entries: "Regjistrat e orarit",
    noEntries: "Nuk ka regjistra të orarit",
    noEntriesText: "Shto orarin e parë për ekipin ose projektin tënd.",
    worker: "Punëtori",
    noWorker: "Pa punëtor të lidhur",
    workerName: "Emri i punëtorit",
    project: "Projekti",
    noProject: "Pa projekt të lidhur",
    projectName: "Emri i projektit",
    date: "Data",
    startTime: "Ora e fillimit",
    endTime: "Ora e përfundimit",
    breakMinutes: "Pushimi (min)",
    hourlyRate: "Çmimi për orë",
    status: "Statusi",
    notes: "Shënime",
    notesPlaceholder: "Përshkrimi i punës, zona, materiali ose detaje shtesë...",
    cancel: "Anulo",
    save: "Ruaj orarin",
    update: "Përditëso orarin",
    deleteConfirm: "A dëshiron ta fshish këtë regjistër?",
    saved: "Orari u ruajt me sukses.",
    updated: "Orari u përditësua me sukses.",
    deleted: "Orari u fshi me sukses.",
    loadError: "Gabim gjatë leximit të orareve.",
    saveError: "Gabim gjatë ruajtjes së orarit.",
    requiredWorker: "Zgjidh ose shkruaj emrin e punëtorit.",
    requiredDate: "Zgjidh datën e orarit.",
    invalidHours: "Kontrollo orën e fillimit, përfundimit ose pushimin.",
    open: "Hapur",
    approved: "Miratuar",
    invoiced: "Faturuar",
    totalEntries: "Gjithsej",
    workedHours: "Orë pune",
    workedCost: "Kosto",
    details: "Detaje",
    linkedData: "Lidhje",
  },
  de: {
    title: "Timesheets / Arbeitszeiten",
    subtitle: "Erfasse Arbeitsstunden für Projekte und Mitarbeiter und berechne die Arbeitskosten.",
    newEntry: "Neuer Zeiteintrag",
    editEntry: "Zeiteintrag bearbeiten",
    totalHours: "Gesamtstunden",
    thisWeek: "Diese Woche",
    activeWorkers: "Aktive Mitarbeiter",
    laborCost: "Arbeitskosten",
    searchPlaceholder: "Nach Mitarbeiter, Projekt oder Notiz suchen",
    allStatuses: "Alle Status",
    entries: "Zeiteinträge",
    noEntries: "Keine Zeiteinträge vorhanden",
    noEntriesText: "Lege den ersten Zeiteintrag für dein Team oder Projekt an.",
    worker: "Mitarbeiter",
    noWorker: "Kein verknüpfter Mitarbeiter",
    workerName: "Mitarbeitername",
    project: "Projekt",
    noProject: "Kein verknüpftes Projekt",
    projectName: "Projektname",
    date: "Datum",
    startTime: "Beginn",
    endTime: "Ende",
    breakMinutes: "Pause (Min)",
    hourlyRate: "Stundensatz",
    status: "Status",
    notes: "Notizen",
    notesPlaceholder: "Beschreibung der Arbeit, Bereich, Material oder weitere Details...",
    cancel: "Abbrechen",
    save: "Zeiteintrag speichern",
    update: "Zeiteintrag aktualisieren",
    deleteConfirm: "Diesen Zeiteintrag wirklich löschen?",
    saved: "Zeiteintrag wurde gespeichert.",
    updated: "Zeiteintrag wurde aktualisiert.",
    deleted: "Zeiteintrag wurde gelöscht.",
    loadError: "Fehler beim Laden der Arbeitszeiten.",
    saveError: "Fehler beim Speichern des Zeiteintrags.",
    requiredWorker: "Bitte einen Mitarbeiter wählen oder eingeben.",
    requiredDate: "Bitte das Datum auswählen.",
    invalidHours: "Bitte Beginn, Ende oder Pause prüfen.",
    open: "Offen",
    approved: "Freigegeben",
    invoiced: "Abgerechnet",
    totalEntries: "Gesamt",
    workedHours: "Arbeitsstunden",
    workedCost: "Kosten",
    details: "Details",
    linkedData: "Verknüpfung",
  },
};

const statuses = ["open", "approved", "invoiced"];

function pad(value) {
  return String(value).padStart(2, "0");
}

function todayIso() {
  const now = new Date();
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

function formatCurrency(value, lang) {
  const locale = lang === "de" ? "de-DE" : "sq-AL";
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));
}

function formatDate(value, lang) {
  if (!value) return "—";
  const d = new Date(`${value}T00:00:00`);
  return new Intl.DateTimeFormat(lang === "de" ? "de-DE" : "sq-AL", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

function getMinutes(time) {
  const [h, m] = String(time || "").split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

function calcHours(entry) {
  const start = getMinutes(entry.startTime);
  const end = getMinutes(entry.endTime);
  const breakMinutes = Number(entry.breakMinutes || 0);

  if (start === null || end === null) return 0;
  const worked = end - start - breakMinutes;
  if (worked <= 0) return 0;
  return Number((worked / 60).toFixed(2));
}

function weekBounds() {
  const now = new Date();
  const day = now.getDay() || 7;
  const monday = new Date(now);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - day + 1);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { start: toIso(monday), end: toIso(sunday) };
}

function normalizeEntry(docSnap) {
  const data = docSnap.data() || {};
  const hours = Number(data.hours ?? calcHours(data) ?? 0);
  const hourlyRate = Number(data.hourlyRate || 0);
  return {
    id: docSnap.id,
    date: data.date || "",
    workerId: data.workerId || "",
    workerName: data.workerName || "",
    projectId: data.projectId || "",
    projectName: data.projectName || "",
    startTime: data.startTime || "08:00",
    endTime: data.endTime || "16:00",
    breakMinutes: String(data.breakMinutes ?? 30),
    hourlyRate: String(hourlyRate || ""),
    status: data.status || "open",
    notes: data.notes || "",
    hours,
    laborCost: Number((hours * hourlyRate).toFixed(2)),
    createdAt: data.createdAt || null,
    updatedAt: data.updatedAt || null,
  };
}

function statusClasses(status, theme) {
  const map = {
    open: theme === "dark" ? "border-amber-400/20 bg-amber-500/10 text-amber-300" : "border-amber-200 bg-amber-50 text-amber-700",
    approved: theme === "dark" ? "border-blue-400/20 bg-blue-500/10 text-blue-300" : "border-blue-200 bg-blue-50 text-blue-700",
    invoiced: theme === "dark" ? "border-emerald-400/20 bg-emerald-500/10 text-emerald-300" : "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  return map[status] || map.open;
}

function TimesheetModal({
  open,
  onClose,
  onSubmit,
  form,
  setForm,
  projects,
  workers,
  lang,
  theme,
  editingId,
}) {
  const text = ui[lang] || ui.sq;

  if (!open) return null;

  const modal = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-950/45 p-3 sm:p-5">
      <div
        className={`flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[30px] border shadow-[0_30px_100px_rgba(15,23,42,0.25)] ${
          theme === "dark" ? "border-slate-800 bg-slate-950 text-slate-100" : "border-slate-200 bg-white text-slate-900"
        }`}
      >
        <div className={`border-b px-5 py-4 sm:px-6 ${theme === "dark" ? "border-slate-800" : "border-slate-200"}`}>
          <h3 className="text-2xl font-black tracking-tight">{editingId ? text.editEntry : text.newEntry}</h3>
          <p className={`mt-1 text-sm ${theme === "dark" ? "text-slate-400" : "text-slate-500"}`}>{text.subtitle}</p>
        </div>

        <form onSubmit={onSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <UILabel>{text.worker}</UILabel>
                <UISelect
                  value={form.workerId}
                  onChange={(e) => {
                    const selected = workers.find((worker) => worker.id === e.target.value);
                    setForm((prev) => ({
                      ...prev,
                      workerId: e.target.value,
                      workerName: selected?.name || prev.workerName,
                      hourlyRate:
                        prev.hourlyRate || selected?.hourlyRate || selected?.rate || "",
                    }));
                  }}
                >
                  <option value="">{text.noWorker}</option>
                  {workers.map((worker) => (
                    <option key={worker.id} value={worker.id}>
                      {worker.name}
                    </option>
                  ))}
                </UISelect>
              </div>

              <div>
                <UILabel>{text.workerName}</UILabel>
                <UIInput
                  value={form.workerName}
                  onChange={(e) => setForm((prev) => ({ ...prev, workerName: e.target.value }))}
                  placeholder={text.workerName}
                />
              </div>

              <div>
                <UILabel>{text.project}</UILabel>
                <UISelect
                  value={form.projectId}
                  onChange={(e) => {
                    const selected = projects.find((project) => project.id === e.target.value);
                    setForm((prev) => ({
                      ...prev,
                      projectId: e.target.value,
                      projectName: selected?.name || prev.projectName,
                    }));
                  }}
                >
                  <option value="">{text.noProject}</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </UISelect>
              </div>

              <div>
                <UILabel>{text.projectName}</UILabel>
                <UIInput
                  value={form.projectName}
                  onChange={(e) => setForm((prev) => ({ ...prev, projectName: e.target.value }))}
                  placeholder={text.projectName}
                />
              </div>

              <div>
                <UILabel>{text.date}</UILabel>
                <UIInput
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm((prev) => ({ ...prev, date: e.target.value }))}
                />
              </div>

              <div>
                <UILabel>{text.status}</UILabel>
                <UISelect
                  value={form.status}
                  onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                >
                  {statuses.map((status) => (
                    <option key={status} value={status}>
                      {text[status]}
                    </option>
                  ))}
                </UISelect>
              </div>

              <div>
                <UILabel>{text.startTime}</UILabel>
                <UIInput
                  type="time"
                  value={form.startTime}
                  onChange={(e) => setForm((prev) => ({ ...prev, startTime: e.target.value }))}
                />
              </div>

              <div>
                <UILabel>{text.endTime}</UILabel>
                <UIInput
                  type="time"
                  value={form.endTime}
                  onChange={(e) => setForm((prev) => ({ ...prev, endTime: e.target.value }))}
                />
              </div>

              <div>
                <UILabel>{text.breakMinutes}</UILabel>
                <UIInput
                  type="number"
                  min="0"
                  value={form.breakMinutes}
                  onChange={(e) => setForm((prev) => ({ ...prev, breakMinutes: e.target.value }))}
                  placeholder="30"
                />
              </div>

              <div>
                <UILabel>{text.hourlyRate}</UILabel>
                <UIInput
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.hourlyRate}
                  onChange={(e) => setForm((prev) => ({ ...prev, hourlyRate: e.target.value }))}
                  placeholder="18.00"
                />
              </div>
            </div>

            <div className="mt-4">
              <UILabel>{text.notes}</UILabel>
              <textarea
                value={form.notes}
                onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
                placeholder={text.notesPlaceholder}
                rows={5}
                className={`w-full rounded-[24px] border px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 ${
                  theme === "dark"
                    ? "border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-500"
                    : "border-slate-200 bg-slate-50 text-slate-900"
                }`}
              />
            </div>
          </div>

          <div className={`border-t px-5 py-4 sm:px-6 ${theme === "dark" ? "border-slate-800" : "border-slate-200"}`}>
            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <UIButton type="button" theme={theme} variant="secondary" onClick={onClose}>
                {text.cancel}
              </UIButton>
              <UIButton type="submit" theme={theme} variant="primary">
                {editingId ? text.update : text.save}
              </UIButton>
            </div>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default function Timesheets({ lang = "sq", t = {} }) {
  const text = ui[lang] || ui.sq;
  const { theme, currentTheme } = usePanelTheme();

  const [entries, setEntries] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState({ ...initialForm, date: todayIso() });

  const loadData = async () => {
    setLoading(true);
    try {
      const [timesheetsSnap, projectsSnap, workersSnap] = await Promise.all([
        getDocs(query(collection(db, "timesheets"), orderBy("date", "desc"))),
        getDocs(collection(db, "projects")),
        getDocs(collection(db, "workers")),
      ]);

      setEntries(timesheetsSnap.docs.map(normalizeEntry));
      setProjects(
        projectsSnap.docs.map((item) => ({
          id: item.id,
          name: item.data()?.name || item.data()?.projectName || "",
        }))
      );
      setWorkers(
        workersSnap.docs.map((item) => ({
          id: item.id,
          name: item.data()?.name || item.data()?.fullName || "",
          hourlyRate: item.data()?.hourlyRate || item.data()?.rate || "",
        }))
      );
    } catch (error) {
      console.error(error);
      notify.error(text.loadError);
    } finally {
      setLoading(false);
    }
  };

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    loadData();
  }, []);
  /* eslint-enable react-hooks/exhaustive-deps */

  const filteredEntries = useMemo(() => {
    const queryText = search.trim().toLowerCase();
    return entries.filter((entry) => {
      const matchesStatus = statusFilter === "all" || entry.status === statusFilter;
      const haystack = [entry.workerName, entry.projectName, entry.notes, entry.date]
        .join(" ")
        .toLowerCase();
      const matchesSearch = !queryText || haystack.includes(queryText);
      return matchesStatus && matchesSearch;
    });
  }, [entries, search, statusFilter]);

  const stats = useMemo(() => {
    const week = weekBounds();
    const totalHours = entries.reduce((sum, entry) => sum + Number(entry.hours || 0), 0);
    const weekHours = entries
      .filter((entry) => entry.date >= week.start && entry.date <= week.end)
      .reduce((sum, entry) => sum + Number(entry.hours || 0), 0);
    const activeWorkers = new Set(entries.map((entry) => entry.workerName).filter(Boolean)).size;
    const laborCost = entries.reduce((sum, entry) => sum + Number(entry.laborCost || 0), 0);
    return {
      totalHours: Number(totalHours.toFixed(2)),
      weekHours: Number(weekHours.toFixed(2)),
      activeWorkers,
      laborCost: Number(laborCost.toFixed(2)),
    };
  }, [entries]);

  const resetForm = () => {
    setEditingId("");
    setForm({ ...initialForm, date: todayIso() });
  };

  const handleOpenCreate = () => {
    resetForm();
    setModalOpen(true);
  };

  const handleEdit = (entry) => {
    setEditingId(entry.id);
    setForm({
      date: entry.date || todayIso(),
      workerId: entry.workerId || "",
      workerName: entry.workerName || "",
      projectId: entry.projectId || "",
      projectName: entry.projectName || "",
      startTime: entry.startTime || "08:00",
      endTime: entry.endTime || "16:00",
      breakMinutes: String(entry.breakMinutes ?? "30"),
      hourlyRate: String(entry.hourlyRate ?? ""),
      status: entry.status || "open",
      notes: entry.notes || "",
    });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(text.deleteConfirm)) return;
    try {
      await deleteDoc(doc(db, "timesheets", id));
      setEntries((prev) => prev.filter((entry) => entry.id !== id));
      notify.success(text.deleted);
    } catch (error) {
      console.error(error);
      notify.error(text.saveError);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!String(form.workerName || "").trim()) {
      notify.error(text.requiredWorker);
      return;
    }

    if (!form.date) {
      notify.error(text.requiredDate);
      return;
    }

    const hours = calcHours(form);
    if (hours <= 0) {
      notify.error(text.invalidHours);
      return;
    }

    const hourlyRate = Number(form.hourlyRate || 0);
    const payload = {
      date: form.date,
      workerId: form.workerId || "",
      workerName: String(form.workerName || "").trim(),
      projectId: form.projectId || "",
      projectName: String(form.projectName || "").trim(),
      startTime: form.startTime || "08:00",
      endTime: form.endTime || "16:00",
      breakMinutes: Number(form.breakMinutes || 0),
      hourlyRate,
      status: form.status || "open",
      notes: String(form.notes || "").trim(),
      hours,
      laborCost: Number((hours * hourlyRate).toFixed(2)),
      updatedAt: serverTimestamp(),
    };

    try {
      if (editingId) {
        await updateDoc(doc(db, "timesheets", editingId), payload);
        setEntries((prev) =>
          prev
            .map((entry) => (entry.id === editingId ? { id: editingId, ...payload, breakMinutes: String(payload.breakMinutes), hourlyRate: String(payload.hourlyRate) } : entry))
            .sort((a, b) => `${b.date} ${b.startTime}`.localeCompare(`${a.date} ${a.startTime}`))
        );
        notify.success(text.updated);
      } else {
        const created = await addDoc(collection(db, "timesheets"), {
          ...payload,
          createdAt: serverTimestamp(),
        });
        setEntries((prev) =>
          [{ id: created.id, ...payload, breakMinutes: String(payload.breakMinutes), hourlyRate: String(payload.hourlyRate) }, ...prev].sort((a, b) => `${b.date} ${b.startTime}`.localeCompare(`${a.date} ${a.startTime}`))
        );
        notify.success(text.saved);
      }

      setModalOpen(false);
      resetForm();
    } catch (error) {
      console.error(error);
      notify.error(text.saveError);
    }
  };

  return (
    <PageShell>
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <SectionHeading title={text.title} subtitle={text.subtitle} />
        <div className="flex justify-start lg:justify-end">
          <UIButton theme={theme} variant="primary" onClick={handleOpenCreate}>
            <Plus className="h-4 w-4" /> {text.newEntry}
          </UIButton>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-2 xl:grid-cols-4">
        {[
          {
            label: text.totalHours,
            value: stats.totalHours,
            icon: Clock3,
            suffix: "h",
            cardClass: theme === "dark" ? "border-blue-500/20 bg-blue-500/10" : "border-blue-200 bg-blue-50/70",
            iconClass: theme === "dark" ? "bg-blue-500/15 text-blue-300" : "bg-blue-100 text-blue-700",
          },
          {
            label: text.thisWeek,
            value: stats.weekHours,
            icon: TimerReset,
            suffix: "h",
            cardClass: theme === "dark" ? "border-violet-500/20 bg-violet-500/10" : "border-violet-200 bg-violet-50/70",
            iconClass: theme === "dark" ? "bg-violet-500/15 text-violet-300" : "bg-violet-100 text-violet-700",
          },
          {
            label: text.activeWorkers,
            value: stats.activeWorkers,
            icon: Users2,
            cardClass: theme === "dark" ? "border-amber-500/20 bg-amber-500/10" : "border-amber-200 bg-amber-50/70",
            iconClass: theme === "dark" ? "bg-amber-500/15 text-amber-300" : "bg-amber-100 text-amber-700",
          },
          {
            label: text.laborCost,
            value: formatCurrency(stats.laborCost, lang),
            icon: Receipt,
            cardClass: theme === "dark" ? "border-emerald-500/20 bg-emerald-500/10" : "border-emerald-200 bg-emerald-50/70",
            iconClass: theme === "dark" ? "bg-emerald-500/15 text-emerald-300" : "bg-emerald-100 text-emerald-700",
          },
        ].map((stat) => {
          const Icon = stat.icon;
          return (
            <PanelCard
              key={stat.label}
              className={stat.cardClass}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className={`text-xs sm:text-sm font-semibold ${currentTheme.muted}`}>{stat.label}</p>
                  <div className={`mt-2 text-3xl sm:text-4xl font-black tracking-tight ${currentTheme.text}`}>
                    {stat.value}
                    {stat.suffix ? <span className="ml-1 text-xl sm:text-2xl">{stat.suffix}</span> : null}
                  </div>
                </div>
                <div className={`flex h-11 w-11 items-center justify-center rounded-2xl ${stat.iconClass}`}>
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </PanelCard>
          );
        })}
      </div>

      <PanelCard>
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="relative w-full lg:max-w-xl">
            <Search className={`pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 ${theme === "dark" ? "text-slate-500" : "text-slate-400"}`} />
            <UIInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={text.searchPlaceholder}
              className="pl-11"
            />
          </div>

          <div className="w-full lg:max-w-[220px]">
            <UISelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">{text.allStatuses}</option>
              {statuses.map((status) => (
                <option key={status} value={status}>
                  {text[status]}
                </option>
              ))}
            </UISelect>
          </div>
        </div>
      </PanelCard>

      <PanelCard>
        <div className="mb-5 flex items-center justify-between gap-3">
          <div>
            <h3 className={`text-xl font-black tracking-tight ${currentTheme.text}`}>{text.entries}</h3>
            <p className={`mt-1 text-sm ${currentTheme.muted}`}>{filteredEntries.length} {text.totalEntries}</p>
          </div>
        </div>

        {loading ? (
          <div className={`rounded-[24px] border border-dashed p-10 text-center text-sm ${theme === "dark" ? "border-slate-800 text-slate-400" : "border-slate-200 text-slate-500"}`}>
            {t.loading || "Loading..."}
          </div>
        ) : !filteredEntries.length ? (
          <div className={`rounded-[24px] border border-dashed p-10 text-center ${theme === "dark" ? "border-slate-800" : "border-slate-200"}`}>
            <div className={`text-lg font-bold ${currentTheme.text}`}>{text.noEntries}</div>
            <p className={`mt-2 text-sm ${currentTheme.muted}`}>{text.noEntriesText}</p>
          </div>
        ) : (
          <div className="grid gap-4 xl:grid-cols-2">
            {filteredEntries.map((entry) => (
              <div
                key={entry.id}
                className={`rounded-[24px] border p-5 ${theme === "dark" ? "border-slate-800 bg-slate-950/60" : "border-slate-200 bg-slate-50/60"}`}
              >
                <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className={`truncate text-lg font-black tracking-tight ${currentTheme.text}`}>{entry.workerName || "—"}</h4>
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses(entry.status, theme)}`}>
                        {text[entry.status] || entry.status}
                      </span>
                    </div>
                    <p className={`mt-1 text-sm ${currentTheme.muted}`}>{formatDate(entry.date, lang)} · {entry.startTime} - {entry.endTime}</p>
                  </div>

                  <div className="flex items-center gap-2 sm:justify-end">
                    <UIButton theme={theme} variant="secondary" size="sm" onClick={() => handleEdit(entry)}>
                      <Pencil className="h-4 w-4" />
                    </UIButton>
                    <UIButton theme={theme} variant="secondary" size="sm" onClick={() => handleDelete(entry.id)}>
                      <Trash2 className="h-4 w-4" />
                    </UIButton>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-3">
                  <div className={`rounded-2xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-white"}`}>
                    <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${currentTheme.muted}`}>{text.workedHours}</div>
                    <div className={`mt-2 text-xl font-black ${currentTheme.text}`}>{entry.hours}h</div>
                  </div>
                  <div className={`rounded-2xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-white"}`}>
                    <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${currentTheme.muted}`}>{text.workedCost}</div>
                    <div className={`mt-2 text-xl font-black ${currentTheme.text}`}>{formatCurrency(entry.laborCost, lang)}</div>
                  </div>
                  <div className={`rounded-2xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-900/70" : "border-slate-200 bg-white"}`}>
                    <div className={`text-xs font-semibold uppercase tracking-[0.18em] ${currentTheme.muted}`}>{text.breakMinutes}</div>
                    <div className={`mt-2 text-xl font-black ${currentTheme.text}`}>{entry.breakMinutes || 0} min</div>
                  </div>
                </div>

                <div className="mt-4 grid gap-3 md:grid-cols-2">
                  <div className={`rounded-2xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white/70"}`}>
                    <div className={`mb-2 text-xs font-semibold uppercase tracking-[0.18em] ${currentTheme.muted}`}>{text.linkedData}</div>
                    <div className="space-y-2 text-sm">
                      <div className={`flex items-center gap-2 ${currentTheme.text}`}>
                        <Users2 className="h-4 w-4" />
                        <span className="truncate">{entry.workerName || "—"}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${currentTheme.text}`}>
                        <BriefcaseBusiness className="h-4 w-4" />
                        <span className="truncate">{entry.projectName || "—"}</span>
                      </div>
                    </div>
                  </div>

                  <div className={`rounded-2xl border px-4 py-3 ${theme === "dark" ? "border-slate-800 bg-slate-900/40" : "border-slate-200 bg-white/70"}`}>
                    <div className={`mb-2 text-xs font-semibold uppercase tracking-[0.18em] ${currentTheme.muted}`}>{text.details}</div>
                    <p className={`text-sm leading-6 ${currentTheme.text}`}>{entry.notes || "—"}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </PanelCard>

      <TimesheetModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          resetForm();
        }}
        onSubmit={handleSubmit}
        form={form}
        setForm={setForm}
        projects={projects}
        workers={workers}
        lang={lang}
        theme={theme}
        editingId={editingId}
      />
    </PageShell>
  );
}
